import { Controller } from "stimulus";
import { snapSlot, firstFreeSlot, resizeSlot, moveSlot, RESIZE_STEP, slotCopy, clockLabel, canBlock, blockFields, bookingHref, returnPath } from "../../training_grooming_calendar/slots";
import { placePopover, visibleViewport } from "../../training_grooming_calendar/popover";
import { openOverlay, closeOverlay } from "../../training_grooming_calendar/overlay";
import { submitBlock, postBlockForm } from "../../training_grooming_calendar/block_submit";

const SKIP_DAY = ".tg-appt, .tg-block, .tg-schedule, .tg-ghost";
const SKIP_WEEK = ".tg-chip, .tg-week-more, .tg-ghost-chip";
const KEY_STEPS = { ArrowUp: -RESIZE_STEP, ArrowDown: RESIZE_STEP };

export default class extends Controller {
  static targets = ["container", "card", "ghost", "ghostChip"];
  static values = { bookingUrl: String };

  openDay(event) {
    if (!this.hasCardTarget || event.target.closest(SKIP_DAY)) return;
    const body = event.currentTarget;
    const bounds = this.windowFor(body);
    const rect = body.getBoundingClientRect();
    const minute = bounds[0] + ((event.clientY - rect.top) / rect.height) * (bounds[1] - bounds[0]);
    const slot = snapSlot(minute, this.busyFor(body), bounds);
    const ghost = this.ghostTarget.content.firstElementChild.cloneNode(true);
    this.show({ source: body, slot, anchor: ghost, place: () => body.appendChild(ghost) });
  }

  openWeek(event) {
    if (!this.hasCardTarget || event.target.closest(SKIP_WEEK)) return;
    const cell = event.currentTarget;
    const slot = firstFreeSlot(this.busyFor(cell), this.windowFor(cell));
    const chip = this.ghostChipTarget.content.firstElementChild.cloneNode(true);
    const more = cell.querySelector(".tg-week-more");
    if (more) cell.dispatchEvent(new CustomEvent("training-grooming:expand-row", { bubbles: true }));
    this.show({ source: cell, slot, anchor: chip, place: () => cell.insertBefore(chip, more) });
  }

  openFirstFree(event) {
    const button = event.currentTarget;
    const from = button.dataset.from ? Number(button.dataset.from) : undefined;
    this.show({ source: button, slot: firstFreeSlot(this.busyFor(button), this.windowFor(button), from), anchor: button });
  }

  close() {
    closeOverlay();
  }

  saveBlock(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const token = document.querySelector('meta[name="csrf-token"]');
    submitBlock({
      button: form.querySelector('button[type="submit"]'),
      send: () => postBlockForm({ form, token: token ? token.content : "" }),
      notify: window.notyf,
      done: () => {
        closeOverlay({ restoreFocus: false });
        this.element.dispatchEvent(new CustomEvent("training-grooming:reload"));
      },
    });
  }

  reset() {
    this.cleanup();
  }

  grab(event) {
    if (!this.ghost) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    handle.focus({ preventScroll: true });
    const [first, last] = this.slotContext.bounds;
    const height = this.ghost.parentElement.getBoundingClientRect().height;
    this.dragging = { edge: handle.dataset.edge, y: event.clientY, slot: { ...this.slot }, perMinute: height / (last - first) };
  }

  drag(event) {
    if (!this.dragging) return;
    const { edge, y, slot, perMinute } = this.dragging;
    const minutes = (event.clientY - y) / perMinute;
    if (edge === "move") {
      this.apply(moveSlot({ ...slot, minutes, busy: this.slotContext.busy, bounds: this.slotContext.bounds }));
    } else {
      this.resize(slot, edge, minutes);
    }
  }

  drop(event) {
    if (!this.dragging) return;
    const handle = event.currentTarget;
    if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
    this.dragging = null;
  }

  swallow(event) {
    event.stopPropagation();
  }

  nudge(event) {
    const minutes = KEY_STEPS[event.key];
    if (!minutes || !this.ghost) return;
    event.preventDefault();
    this.resize(this.slot, event.currentTarget.dataset.edge, minutes);
  }

  resize(from, edge, minutes) {
    this.apply(resizeSlot({ ...from, edge, minutes, busy: this.slotContext.busy, bounds: this.slotContext.bounds }));
  }

  apply(slot) {
    if (slot.start === this.slot.start && slot.end === this.slot.end) return;
    this.slot = slot;
    this.fill();
    this.paintGhost();
    this.position(this.ghost);
  }

  show({ source, slot, anchor, place = null }) {
    if (!this.hasCardTarget) return;
    const sheet = this.mobile();
    closeOverlay({ restoreFocus: false });
    this.slotContext = {
      lane: source.closest("[data-lane-key]").dataset,
      date: source.dataset.date,
      dayLabel: source.dataset.dayLabel,
      busy: this.busyFor(source),
      bounds: this.windowFor(source),
    };
    this.slot = slot;
    if (!this.fill()) return;
    this.extras = [];
    if (place && !sheet) {
      if (anchor.classList.contains("tg-ghost")) {
        this.ghost = anchor;
        this.paintGhost();
      }
      place();
      this.extras.push(anchor);
    }
    const card = this.cardTarget;
    card.hidden = false;
    openOverlay({ element: card, anchor: place ? source : anchor, ignore: [source], root: this.element, sheet, onClose: () => this.cleanup() });
    if (!sheet) this.position(anchor);
    const first = card.querySelector(".tg-qa-tile");
    if (first) first.focus({ preventScroll: true });
  }

  fill() {
    const card = this.cardTarget;
    const { lane, date, dayLabel } = this.slotContext;
    const slot = this.slot;
    const kind = lane.laneKind;
    this.copy = slotCopy({ kind, role: lane.laneRole, name: lane.laneName, dayLabel, start: slot.start, end: slot.end });
    ["kicker", "when", "who", "book-sub", "block-sub"].forEach((slotName) => {
      const node = card.querySelector(`[data-slot="${slotName}"]`);
      if (node) node.textContent = this.copy[slotName.replace(/-(\w)/, (_match, letter) => letter.toUpperCase())];
    });
    card.classList.toggle("tg-qa-room", kind === "room");
    const book = card.querySelector('[data-slot="book"]');
    if (book) book.href = bookingHref(this.bookingUrlValue, { date, slot, returnTo: returnPath(window.location) });
    const form = card.querySelector('[data-slot="block"]');
    if (form) this.fillBlockForm(form, { kind, laneId: lane.laneId, location: lane.laneLocation, you: lane.laneYou === "true", date, slot });
    return !!book || (!!form && !form.hidden);
  }

  fillBlockForm(form, { kind, laneId, location, you, date, slot }) {
    form.hidden = !canBlock({ kind, you, employee: this.cardTarget.dataset.employee === "true" });
    const field = (name) => form.querySelector(`[data-field="${name}"]`);
    Object.entries(blockFields({ kind, laneId, location, fallbackLocation: form.dataset.defaultLocation, date, slot })).forEach(([name, value]) => {
      field(name).value = value;
    });
    field("user_id").disabled = kind !== "staff";
    field("location_resource_id").disabled = kind !== "room";
    field("return_to").value = returnPath(window.location);
  }

  paintGhost() {
    const ghost = this.ghost;
    const { start, end } = this.slot;
    const [first, last] = this.slotContext.bounds;
    ghost.style.setProperty("--start", start - first);
    ghost.style.setProperty("--length", end - start);
    ghost.querySelector(".tg-ghost-label").textContent = this.copy.ghost;
    ghost.querySelectorAll(".tg-ghost-handle").forEach((handle) => {
      const value = handle.dataset.edge === "start" ? start : end;
      handle.setAttribute("aria-valuemin", String(first));
      handle.setAttribute("aria-valuemax", String(last));
      handle.setAttribute("aria-valuenow", String(value));
      handle.setAttribute("aria-valuetext", clockLabel(value));
    });
  }

  position(anchor) {
    const card = this.cardTarget;
    const { left, top } = placePopover({
      anchor: anchor.getBoundingClientRect(),
      container: this.containerTarget.getBoundingClientRect(),
      viewport: visibleViewport(),
      width: card.offsetWidth,
      height: card.offsetHeight,
    });
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
  }

  cleanup() {
    if (this.hasCardTarget) this.cardTarget.hidden = true;
    (this.extras || []).forEach((node) => node.remove());
    this.extras = [];
    this.ghost = null;
    this.dragging = null;
  }

  busyFor(element) {
    return JSON.parse(element.dataset.busy || "[]");
  }

  windowFor(element) {
    return JSON.parse(element.closest("[data-window]").dataset.window);
  }

  mobile() {
    return !!this.containerTarget.querySelector(".tg-mobile");
  }
}
